import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import AuthService from "services/auth-service";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import { useMaterialUIController } from "context";
import moment from "moment";

function Summaries({ color, title, categories, type, direction, selectedYear, selectedAccount }) {
    const [controller] = useMaterialUIController();
    const { accounts } = controller;
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [line_chart_data, setLineChartData] = useState([]);
    const [total_agg_chart_data, setTotalChartData] = useState([]);
    const [avg_agg_chart_data, setAvgChartData] = useState([]);

    useEffect(() => { fetchData() }, [selectedAccount, selectedYear]);

    const fetchData = async () => {
        const payload = {
            ...(selectedAccount?.id && { account_id: selectedAccount.id }),
            ...(selectedYear && { from: `${selectedYear}01` }),
            ...(selectedYear && { to: `${selectedYear}12` }),
        };
        let data = await AuthService.fetchSummaries(type, payload);
        data = await calculateNextMonthPrediction(data);
        data = addTotalPerKey(data);
        transformLineDataForChart(data);
        (type === "first_day_snapshot") ? transformBarDataForChartForSnapshot(data) : transformBarDataForChart(data);
        data = calculateAverage(data);
        transformOutputForTable(data);
    };

    const transformLineDataForChart = (data) => {
        let months = [];
        let amounts = [];
        for (const key in data) {
            months.push(key);
            let amount = data[key].find(item => item.category === "total").amount / 100;
            amount = type == "balance" ? amount : Math.abs(amount);
            amounts.push(amount);
        }
        setLineChartData({
            data: {
                labels: direction === -1 ? months.reverse() : months,
                datasets: { 
                    label: title, 
                    data: direction === -1 ? amounts.reverse() : amounts,
                }
            }
        });
    };

    const transformBarDataForChartForSnapshot = (data) => {
        const keys = Object.keys(data).reduce((acc, key) => {
            let year = key.slice(0, 4);
            acc[year] = acc[year] > key ? acc[year] : key;
            return acc;
        }, {});

        const aggregatedByYear = Object.values(keys).reduce((acc, month) => {
            acc[month.slice(0, 4)] = {
                total: data[month].find(item => item.category === "total").amount / 100,
                count: 1
            };
            return acc;
        }, {});

        const avgByYear = Object.keys(aggregatedByYear).reduce((acc, year) => {
            acc[year] = aggregatedByYear[year].total - (aggregatedByYear[year-1] ? aggregatedByYear[year-1].total : 0);
            return acc;
        }, {});
        
        setTotalChartData({
            data: {
                labels: Object.keys(aggregatedByYear).reverse(),
                datasets: {
                    label: title, 
                    data: Object.values(aggregatedByYear).map(yearData => yearData.total).reverse() // Use total values for chart
                }
            }
        });
        
        setAvgChartData({
            data: {
                labels: Object.keys(avgByYear).reverse(),
                datasets: {
                    label: `${title} Average`, 
                    data: Object.values(avgByYear).reverse() // Use avg values for chart
                }
            }
        });
    }

    const transformBarDataForChart = (data) => {
        const aggregatedByYear = Object.keys(data).reduce((acc, month) => {
            let year = month.substring(0, 4);
            let amount = data[month].find(item => item.category === "total").amount / 100;
            amount = Math.round(type === "balance" ? amount : Math.abs(amount));
        
            acc[year] = acc[year] || { total: 0, count: 0 };
            acc[year].total += amount;
            acc[year].count += 1;
        
            return acc;
        }, {});

        const avgByYear = Object.keys(aggregatedByYear).reduce((acc, year) => {
            acc[year] = aggregatedByYear[year].total / aggregatedByYear[year].count;
            return acc;
        }, {});
        
        setTotalChartData({
            data: {
                labels: Object.keys(aggregatedByYear).reverse(),
                datasets: {
                    label: title, 
                    data: Object.values(aggregatedByYear).map(yearData => yearData.total).reverse()
                }
            }
        });
        
        setAvgChartData({
            data: {
                labels: Object.keys(avgByYear).reverse(),
                datasets: {
                    label: `${title} Average`, 
                    data: Object.values(avgByYear).reverse()
                }
            }
        });
    };

    const transformOutputForTable = (data) => {
        const dates = Object.keys(data);
        const newColumns = [{ 
            Header: "Category", 
            accessor: "category", 
            align: "left", 
            Cell: ({ value }) => <span style={{ position: "sticky", left: 0, zIndex: 10 }}>{value}</span> 
        }];
        
        dates.reverse().forEach((date) => { 
            const bgColor = date === "average" ? "#ffa500cf" : date.includes("EST") ? "#ffa50080" : "transparent";
            newColumns.push({ Header: date, accessor: date, align: "center", bgColor: bgColor });
        });

        const newRows = categories.map((category) => {
            const row = { category };
            dates.forEach((date) => {
                const data2 = data[date].find((item) => item.category === category);
                row[date] = data2 ? formatCurrency(data2.amount / 100) : "$0.00";
            });
            return row;
        });

        setColumns(newColumns);
        setRows(newRows);
    };

    const addTotalPerKey = (data) => {
        for (const key in data) {
        const monthData = data[key];
        monthData.push({
            category: "total",
            amount: monthData.reduce((acc, item) => acc + item.amount, 0)
        });
        }
        return data;
    };

    const calculateNextMonthPrediction = async (data) => {
        if (type !== "outcome" || moment().format("D") < 15) {
            return data;
        }
        
        const month = moment().format("YYYYMM");
        if (!Array.isArray(data?.[month])) {
            return data;
        }

        const monthData = data[month].reduce((acc, item) => {
            if (item.category !== "credit_card") {
                acc.push(item);
            }
            return acc;
        }, []);

        if (!monthData) {
            return data;
        }

        const ccSpends = await Promise.all(accounts.filter((account) => account.type === "card").map(async (account) => {
            return await fetchTotalTransactions(account);
        }));
        monthData.push({
            category: "credit_card",
            amount: ccSpends.reduce((sum, current) => sum + current, 0)
        });

        data[moment().add(1, "month").format("YYYYMM")+" EST"] = monthData;
        return data;
    };

    const fetchTotalTransactions = async (account) => {
        const month_gap = moment().format('D') < account.start_day ? -1 : 0;
        const payload = {
            from: moment().set("date", account.start_day).add(month_gap, "month").format("YYYY-MM-DD"),
            to: moment().set("date", account.start_day).add(month_gap + 1, "month").format("YYYY-MM-DD"),
            accounts: [account.id],
        };
        const response = await AuthService.fetchTotalTransactions(payload);
        return response ?? 0;
    };

    const calculateAverage = (data) => {
        if (type === "first_day_snapshot") {
            return data;
        }
        const total = Object.values(data).length;
        const groupedData = Object.values(data)
            .flat()
            .reduce((acc, transaction) => {
                const { category, amount } = transaction;
                acc[category] = acc[category] || [];
                acc[category].push(amount);
                return acc;
            }, {});
        data["average"] = Object.entries(groupedData).map(([category, amounts]) => {
            const sum = amounts.reduce((acc, amount) => acc + amount, 0);
            const amount = Math.round(sum / total);
            data["average"]
            return { category, amount };
        });
        return data;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", {
            style: 'currency',
            currency: selectedAccount?.currency ?? "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(type == "balance" ? amount : Math.abs(amount));
    };

    return (
        <Grid container spacing={6} mt={0}>
            <Grid item xs={12}>
                <Card>
                    <MDBox mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor={color} borderRadius="lg" coloredShadow="info">
                        <MDTypography variant="h6" color="white">{title} Summaries</MDTypography>
                    </MDBox>
                    <MDBox pt={3} sx={{ overflowX: "auto", display: "block" }}>
                        <DataTable table={{ columns, rows }} isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder />
                        <ReportsLineChart color={color} chart={line_chart_data.data} />
                    </MDBox>
                    <Grid container spacing={3}>
                        <Grid item xs={6} md={6}>
                            <ReportsBarChart color={color} chart={total_agg_chart_data.data} />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <ReportsBarChart color={color} chart={avg_agg_chart_data.data} />
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
        </Grid>
    );
}

Summaries.defaultProps = {
    color: "error",
    categories: ["total", "credit_card", "cash_withdrawal", "rent", "health", "other"],
    title: "Outcome",
    type: "outcome",
    direction: -1
};

Summaries.propTypes = {
    type: PropTypes.oneOf(["outcome", "income", "balance", "first_day_snapshot"]),
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    direction: PropTypes.oneOf([1, -1]),
    title: PropTypes.string,
    categories: PropTypes.array,
    selectedYear: PropTypes.string,
    selectedAccount: PropTypes.object,
};

export default Summaries;
